// FloatingNotepad — Phase 2 of the My Dashboard Notepad. A quick-access,
// always-on-top sticky-notes panel opened from a button in the top banner
// (next to the notification bell). Shares the same localStorage store as the
// dashboard Notepad bar, so notes appear in both. Browser-only, no server.
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { StickyNote, Plus, X, Minus, GripVertical } from "lucide-react";

const KEY = "t2_notes";
const OPEN_KEY = "t2_notes_open";
const POS_KEY = "t2_notes_pos";
const CHANGED = "t2-notes-changed"; // same-tab sync between the two surfaces

const COLOURS = [
  { k: "yellow", cls: "bg-yellow-500/15 border-yellow-500/40" },
  { k: "teal", cls: "bg-teal-500/15 border-teal-500/40" },
  { k: "orange", cls: "bg-orange-500/15 border-orange-500/40" },
  { k: "rose", cls: "bg-rose-500/15 border-rose-500/40" },
  { k: "slate", cls: "bg-slate-500/15 border-slate-500/40" },
];
const colCls = (k) => (COLOURS.find((c) => c.k === k) || COLOURS[0]).cls;
const loadNotes = () => { try { return JSON.parse(localStorage.getItem(KEY) || "[]"); } catch { return []; } };
const loadPos = () => { try { return JSON.parse(localStorage.getItem(POS_KEY) || "null"); } catch { return null; } };

export default function FloatingNotepad() {
  const [open, setOpen] = useState(() => localStorage.getItem(OPEN_KEY) === "1");
  const [notes, setNotes] = useState(loadNotes);
  const [pos, setPos] = useState(loadPos); // {left, top} once dragged, else default top-right
  const drag = useRef(null);

  useEffect(() => { localStorage.setItem(OPEN_KEY, open ? "1" : "0"); }, [open]);

  // Persist + broadcast note changes; also absorb changes from the dashboard bar.
  const commit = useCallback((next) => {
    setNotes(next);
    localStorage.setItem(KEY, JSON.stringify(next));
    window.dispatchEvent(new Event(CHANGED));
  }, []);
  useEffect(() => {
    const reload = () => setNotes(loadNotes());
    window.addEventListener(CHANGED, reload);
    window.addEventListener("storage", reload);
    return () => { window.removeEventListener(CHANGED, reload); window.removeEventListener("storage", reload); };
  }, []);

  const add = () => commit([{ id: Date.now().toString(36), title: "", body: "", colour: "yellow" }, ...notes]);
  const patch = (id, p) => commit(notes.map((x) => (x.id === id ? { ...x, ...p } : x)));
  const del = (id) => commit(notes.filter((x) => x.id !== id));

  // Drag by the header.
  const onDragStart = (e) => {
    const card = e.currentTarget.closest("[data-floating-notepad]");
    const r = card.getBoundingClientRect();
    drag.current = { dx: e.clientX - r.left, dy: e.clientY - r.top };
    const move = (ev) => {
      const left = Math.max(8, Math.min(window.innerWidth - r.width - 8, ev.clientX - drag.current.dx));
      const top = Math.max(8, Math.min(window.innerHeight - 60, ev.clientY - drag.current.dy));
      setPos({ left, top });
    };
    const up = () => {
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", up);
      setPos((p) => { if (p) localStorage.setItem(POS_KEY, JSON.stringify(p)); return p; });
    };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
  };

  const unread = notes.length;
  const style = pos ? { left: pos.left, top: pos.top } : { right: 16, top: 64 };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`relative inline-flex items-center justify-center w-9 h-9 rounded-lg border transition-colors ${open ? "border-yellow-400/50 bg-yellow-500/15 text-yellow-200" : "border-white/10 text-slate-300 hover:text-white hover:bg-white/[0.06]"}`}
        title="Quick notes"
        data-testid="floating-notepad-toggle"
      >
        <StickyNote className="w-4 h-4" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[17px] h-[17px] px-1 rounded-full bg-yellow-400 text-black text-[10px] font-bold grid place-items-center ring-2 ring-[#0F1A30]" data-testid="floating-notepad-count">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && createPortal(
        <div
          data-floating-notepad
          data-testid="floating-notepad-panel"
          className="fixed z-[300] w-[340px] max-w-[92vw] rounded-xl bg-[#0F1A30] border border-yellow-500/25 shadow-[0_24px_70px_rgba(0,0,0,0.6)] overflow-hidden"
          style={style}
        >
          <div
            className="flex items-center gap-2 px-3 py-2 border-b border-white/10 bg-white/[0.03] cursor-move select-none"
            onMouseDown={onDragStart}
            data-testid="floating-notepad-header"
          >
            <GripVertical className="w-3.5 h-3.5 text-slate-500" />
            <StickyNote className="w-3.5 h-3.5 text-yellow-300" />
            <span className="text-[12.5px] font-semibold text-white">Quick Notes</span>
            <span className="text-[10px] text-slate-500 font-mono">· this browser</span>
            <div className="ml-auto flex items-center gap-1">
              <button onClick={add} title="Add note" data-testid="floating-notepad-add" className="p-1 rounded text-teal-300 hover:bg-teal-500/15"><Plus className="w-4 h-4" /></button>
              <button onClick={() => setOpen(false)} title="Hide" data-testid="floating-notepad-close" className="p-1 rounded text-slate-400 hover:text-white hover:bg-white/5"><Minus className="w-4 h-4" /></button>
            </div>
          </div>

          <div className="max-h-[60vh] overflow-y-auto p-2.5 space-y-2.5">
            {notes.length === 0 ? (
              <div className="text-center py-8 text-[12px] text-slate-500">No notes yet — hit + to jot a quick one.</div>
            ) : (
              notes.map((note) => (
                <div key={note.id} className={`rounded-lg border p-2.5 ${colCls(note.colour)}`} data-testid={`floating-note-${note.id}`}>
                  <div className="flex items-center gap-1 mb-1">
                    <input value={note.title} onChange={(e) => patch(note.id, { title: e.target.value })} placeholder="Title"
                      className="flex-1 bg-transparent outline-none text-[12.5px] font-semibold text-slate-100 placeholder:text-slate-500" />
                    <button onClick={() => del(note.id)} data-testid={`floating-note-del-${note.id}`} className="p-0.5 text-slate-400 hover:text-rose-300"><X className="w-3.5 h-3.5" /></button>
                  </div>
                  <textarea value={note.body} onChange={(e) => patch(note.id, { body: e.target.value })} placeholder="Jot something…" rows={3}
                    className="w-full bg-transparent outline-none resize-none text-[12px] text-slate-200 placeholder:text-slate-500" />
                  <div className="flex items-center gap-1.5 mt-1">
                    {COLOURS.map((c) => (
                      <button key={c.k} onClick={() => patch(note.id, { colour: c.k })} title={c.k}
                        className={`w-3.5 h-3.5 rounded-full border ${c.cls} ${note.colour === c.k ? "ring-1 ring-white/60" : ""}`} />
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
